import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Search, ExternalLink, User, Briefcase } from 'lucide-react';

const dealLabel = d => [d.client_name || d.owner_name, d.type].filter(Boolean).join(' · ') || 'Negocio';

export const TaskLinkPicker = ({ form, onChange, onOpen }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    Promise.all([api.get('/crm/leads?show_all=true').catch(() => []), api.get('/deals').catch(() => [])]).then(([l, d]) => {
      const leads = (Array.isArray(l) ? l : l?.leads || []).map(x => ({ kind: 'lead', id: x.id, name: x.full_name || x.phone || 'Lead', sub: x.phone }));
      const deals = (Array.isArray(d) ? d : []).map(x => ({ kind: 'deal', id: x.id, name: dealLabel(x) }));
      setItems([...leads, ...deals]);
    });
  }, []);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return items.filter(i => `${i.name} ${i.sub || ''}`.toLowerCase().includes(s)).slice(0, 8);
  }, [q, items]);

  const choose = i => {
    onChange(i.kind === 'lead'
      ? { lead_id: i.id, lead_name: i.name, deal_id: '', deal_name: '' }
      : { deal_id: i.id, deal_name: i.name, lead_id: '', lead_name: '' });
    setQ('');
  };
  const clear = () => onChange({ lead_id: '', lead_name: '', deal_id: '', deal_name: '' });
  const linked = form.lead_id ? { kind: 'lead', name: form.lead_name, path: `/crm/${form.lead_id}` }
    : form.deal_id ? { kind: 'deal', name: form.deal_name, path: `/negocios/${form.deal_id}` } : null;

  return (
    <div>
      <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Lead o negocio vinculado</Label>
      {linked ? (
        <div data-testid="task-link-current" className="flex items-center gap-2 p-3 rounded-xl bg-[#16302B] border border-[#A38560]/30">
          {linked.kind === 'lead' ? <User size={14} className="text-[#A38560]" /> : <Briefcase size={14} className="text-[#A38560]" />}
          <span className="text-sm text-white flex-1 truncate">{linked.name}</span>
          <button onClick={() => onOpen(linked.path)} data-testid="task-link-open" className="text-white/50 hover:text-[#A38560]" title="Abrir"><ExternalLink size={14} /></button>
          <button onClick={clear} data-testid="task-link-clear" className="text-white/50 hover:text-red-400" title="Quitar"><X size={14} /></button>
        </div>
      ) : (
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input data-testid="task-link-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar lead o negocio por nombre o teléfono"
            className="bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 h-11 rounded-xl pl-9" />
          {results.length > 0 && (
            <div className="mt-1 rounded-xl border border-white/10 bg-[#0A1F1A] overflow-hidden">
              {results.map(i => (
                <button key={`${i.kind}-${i.id}`} onClick={() => choose(i)} data-testid={`task-link-option-${i.id}`}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-white/80 hover:bg-[#16302B]">
                  {i.kind === 'lead' ? <User size={13} className="text-white/40" /> : <Briefcase size={13} className="text-white/40" />}
                  <span className="truncate flex-1">{i.name}</span>
                  <span className="text-[10px] uppercase text-white/30">{i.kind === 'lead' ? 'Lead' : 'Negocio'}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
